import { Textarea } from '../components/ui/textarea';
import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { reportsAPI, barbersAPI, cashCutsAPI, commissionsAPI, salesAPI, expensesAPI, cashRegisterAPI } from '../lib/api';
import CashCutCard from '../components/CashCutCard';
import { formatCurrency, formatDate, formatDateTime, getStartOfMonth, getEndOfMonth, paymentMethodLabels, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { 
  BarChart3, 
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  CalendarDays,
  Receipt,
  Loader2,
  UserCheck,
  Trophy,
  Sparkles,
  Package,
  Scissors,
  FileSpreadsheet,
  FileText,
  Wallet,
  BadgeDollarSign,
  History,
  ArrowDownCircle,
  ArrowUpCircle,
  MinusCircle,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Trash2,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { exportToExcel, exportToPDF } from '../lib/exportUtils';
import { Link } from 'react-router-dom';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { toast } from 'sonner';

// Distinct vivid colors per payment method (light-theme friendly)
const PAYMENT_COLORS = {
  cash: '#10b981',        // emerald (efectivo)
  card: '#3b82f6',        // blue (tarjeta genérica)
  debit: '#0ea5e9',       // sky (TDD)
  credit: '#8b5cf6',      // violet (TDC)
  amex: '#f59e0b',        // amber (Amex)
  transfer: '#14b8a6',    // teal (transferencia)
  mixed: '#ec4899',       // pink (mixto)
};
const FALLBACK_COLORS = ['#6366f1', '#f43f5e', '#84cc16', '#06b6d4', '#a855f7'];

export default function ReportesPage() {
  const { selectedBusiness, isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(getStartOfMonth());
  const [endDate, setEndDate] = useState(getEndOfMonth());
  const [salesReport, setSalesReport] = useState(null);
  const [barbersReport, setBarbersReport] = useState([]);
  const [todayReport, setTodayReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [cashCuts, setCashCuts] = useState([]);
  const [closingCaja, setClosingCaja] = useState(false);
  const [payingBarberId, setPayingBarberId] = useState(null);
  const [historyBarber, setHistoryBarber] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Transacciones tab — unified list of sales + expenses + withdrawals
  // filtered by a date range preset (day/week/month/custom). See loader
  // below (loadTransactions) for how sources are merged into a single
  // timeline suited for a table view.
  const [txRange, setTxRange] = useState('month'); // 'day' | 'week' | 'month' | 'custom'
  const [txStart, setTxStart] = useState(getStartOfMonth());
  const [txEnd, setTxEnd] = useState(getEndOfMonth());
  const [txFilter, setTxFilter] = useState('all'); // 'all' | 'sale' | 'expense' | 'withdrawal'
  const [txLoading, setTxLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);

  const applyTxPreset = (preset) => {
    setTxRange(preset);
    const now = new Date();
    const cdmxToday = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'America/Mexico_City' })
    );
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === 'day') {
      const t = iso(cdmxToday);
      setTxStart(t); setTxEnd(t);
    } else if (preset === 'week') {
      const d = new Date(cdmxToday);
      const dow = d.getDay() === 0 ? 6 : d.getDay() - 1; // Mon=0..Sun=6
      d.setDate(d.getDate() - dow);
      setTxStart(iso(d)); setTxEnd(iso(cdmxToday));
    } else if (preset === 'month') {
      setTxStart(getStartOfMonth()); setTxEnd(getEndOfMonth());
    }
    // 'custom' → leave dates as-is
    void now;
  };

  const loadTransactions = useCallback(async () => {
    if (!selectedBusiness) return;
    setTxLoading(true);
    try {
      // Extend end_date to include the entire CDMX day so late-evening sales
      // (which are stored as UTC ISO after midnight CDMX) don't get clipped.
      const endInclusive = `${txEnd}T23:59:59.999999`;
      const [salesRes, expRes, movRes] = await Promise.all([
        salesAPI.getAll({ start_date: txStart, end_date: endInclusive, include_cancelled: true, limit: 2000 }),
        expensesAPI.getAll({ start_date: txStart, end_date: txEnd }),
        cashRegisterAPI.getMovements({ start_date: txStart, end_date: endInclusive, types: 'withdrawal' }),
      ]);
      const rows = [];
      (salesRes.data || []).forEach((s) => {
        rows.push({
          id: `sale-${s.id}`,
          saleId: s.id,
          sale: s,
          ts: s.created_at,
          kind: 'sale',
          concept: s.ticket_number
            ? `Venta #${s.ticket_number}` + (s.client_name ? ` — ${s.client_name}` : '')
            : `Venta — ${s.client_name || 'walk-in'}`,
          method: s.payment_method || '—',
          amount: Number(s.total || 0),
          sign: +1,
          cancelled: !!s.is_cancelled,
          pendingTotal: !!s.pending_total_authorization,
          pendingCancel: !!s.pending_cancel_authorization,
        });
      });
      (expRes.data || []).forEach((e) => {
        rows.push({
          id: `exp-${e.id}`,
          ts: e.created_at || e.date,
          kind: 'expense',
          concept: `${e.category || 'Gasto'}${e.description ? ' — ' + e.description : ''}`,
          method: e.payment_method || '—',
          amount: Number(e.amount || 0),
          sign: -1,
        });
      });
      (movRes.data || []).forEach((m) => {
        rows.push({
          id: `mov-${m.id}`,
          ts: m.created_at,
          kind: 'withdrawal',
          concept: `Retiro — ${m.reason || m.description || 'sin motivo'}`,
          method: 'cash',
          amount: Math.abs(Number(m.amount || 0)),
          sign: -1,
        });
      });
      rows.sort((a, b) => (a.ts < b.ts ? 1 : -1));
      setTransactions(rows);
    } catch (err) {
      logger.error('Failed to load transactions:', err);
      toast.error('No se pudieron cargar las transacciones');
    } finally {
      setTxLoading(false);
    }
  }, [selectedBusiness, txStart, txEnd]);

  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  // Sale action dialogs shared by the Transacciones tab.
  // Mirrors /caja: audit log, edit total (requires master admin approval)
  // and cancel sale (requires master admin approval).
  const canEditTotal = user?.is_master_admin || user?.role === 'admin';
  const [auditSale, setAuditSale] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [editTotalSale, setEditTotalSale] = useState(null);
  const [newTotalInput, setNewTotalInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [cancelSale, setCancelSale] = useState(null);
  const [cancelReasonInput, setCancelReasonInput] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const openAuditLog = async (sale) => {
    setAuditSale(sale);
    setAuditEntries([]);
    setAuditLoading(true);
    try {
      const { data } = await salesAPI.getAudits(sale.id);
      setAuditEntries(data?.audits || []);
    } catch (err) {
      logger.error('Error loading audits:', err);
      toast.error('No se pudo cargar la auditoría');
    } finally {
      setAuditLoading(false);
    }
  };

  const openEditTotal = (sale) => {
    setEditTotalSale(sale);
    setNewTotalInput(String(sale.total ?? ''));
    setReasonInput('');
  };
  const closeEditTotal = () => { setEditTotalSale(null); setNewTotalInput(''); setReasonInput(''); };
  const submitEditTotal = async () => {
    if (!editTotalSale) return;
    const parsed = parseFloat(newTotalInput);
    if (Number.isNaN(parsed) || parsed < 0) return toast.error('Ingresa un monto válido');
    if (Math.abs(parsed - Number(editTotalSale.total || 0)) < 0.001) return toast.error('El nuevo monto es igual al actual');
    if (!reasonInput.trim() || reasonInput.trim().length < 3) return toast.error('El motivo es obligatorio (mínimo 3 caracteres)');
    setEditSubmitting(true);
    try {
      await salesAPI.requestTotalChange(editTotalSale.id, parsed, reasonInput.trim());
      toast.success('Solicitud enviada. El master admin debe autorizarla.');
      closeEditTotal();
      loadTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openCancelSale = (sale) => { setCancelSale(sale); setCancelReasonInput(''); };
  const closeCancelSale = () => { setCancelSale(null); setCancelReasonInput(''); };
  const submitCancelSale = async () => {
    if (!cancelSale) return;
    const reason = cancelReasonInput.trim();
    if (reason.length < 3) return toast.error('El motivo es obligatorio (mínimo 3 caracteres)');
    setCancelSubmitting(true);
    try {
      await salesAPI.requestCancel(cancelSale.id, reason);
      toast.success('Venta cancelada. Pendiente de autorización del master admin.');
      closeCancelSale();
      loadTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error');
    } finally {
      setCancelSubmitting(false);
    }
  };

  // Change a sale's payment method inline from the Transacciones table.
  // Mirrors CajaPage flow: PATCH /sales/{id}/payment-method, then refresh.
  const handleTxChangePaymentMethod = async (row, newMethod) => {
    if (!row?.saleId || row.method === newMethod) return;
    try {
      await salesAPI.updatePaymentMethod(row.saleId, newMethod);
      toast.success(
        `Método actualizado: ${paymentMethodLabels[row.method] || row.method} → ${paymentMethodLabels[newMethod] || newMethod}`
      );
      loadTransactions();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const human = typeof detail === 'string'
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : (err.message || 'Error desconocido');
      logger.error('Failed to change payment method:', { status, detail, err });
      toast.error(status ? `No se pudo actualizar (${status}): ${human}` : `No se pudo actualizar: ${human}`);
    }
  };

  // Rankings / Clientes tabs — independent date range (default: last 30 days)
  const [rankStartDate, setRankStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });
  });
  const [rankEndDate, setRankEndDate] = useState(() =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' })
  );
  const [rankPreset, setRankPreset] = useState('30');
  const [customersReport, setCustomersReport] = useState(null);
  const [rankingsReport, setRankingsReport] = useState(null);
  const [referralsReport, setReferralsReport] = useState(null);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [performanceReport, setPerformanceReport] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [perfBarberFilter, setPerfBarberFilter] = useState('all');
  const [perfBarbers, setPerfBarbers] = useState([]);
  const [perfExpanded, setPerfExpanded] = useState(null);  // barber_id of expanded row
  const [topClientsReport, setTopClientsReport] = useState(null);
  const [topClientsLoading, setTopClientsLoading] = useState(false);
  const [topClientsLimit, setTopClientsLimit] = useState(10);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [rankingsLoading, setRankingsLoading] = useState(false);

  // Helper: format a Date to YYYY-MM-DD in CDMX
  const cdmxDate = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

  const loadDailyReport = useCallback(async () => {
    if (!selectedBusiness) return;
    try {
      // Only OPEN sales of today (excludes already-closed-in-cuts)
      const res = await reportsAPI.getOpenSales();
      setTodayReport(res.data);
    } catch (error) {
      logger.error('Error loading daily report:', error);
    }
  }, [selectedBusiness]);

  const loadCashCuts = useCallback(async () => {
    if (!selectedBusiness) return;
    try {
      const res = await cashCutsAPI.list();
      setCashCuts(res.data || []);
    } catch (error) {
      logger.error('Error loading cash cuts:', error);
    }
  }, [selectedBusiness]);

  const handleRankPreset = (days) => {
    setRankPreset(String(days));
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Number(days));
    setRankStartDate(start.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }));
    setRankEndDate(end.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }));
  };

  const loadCustomers = useCallback(async () => {
    if (!selectedBusiness) return;
    setCustomersLoading(true);
    try {
      const { data } = await reportsAPI.getCustomers(rankStartDate, rankEndDate);
      setCustomersReport(data);
    } catch (error) {
      logger.error('Error loading customers report:', error);
      toast.error('No se pudo cargar el reporte de clientes');
    } finally {
      setCustomersLoading(false);
    }
  }, [selectedBusiness, rankStartDate, rankEndDate]);

  const loadRankings = useCallback(async () => {
    if (!selectedBusiness) return;
    setRankingsLoading(true);
    try {
      const { data } = await reportsAPI.getRankings(rankStartDate, rankEndDate);
      setRankingsReport(data);
    } catch (error) {
      logger.error('Error loading rankings report:', error);
      toast.error('No se pudo cargar el ranking');
    } finally {
      setRankingsLoading(false);
    }
  }, [selectedBusiness, rankStartDate, rankEndDate]);

  const loadReferrals = useCallback(async () => {
    if (!selectedBusiness) return;
    setReferralsLoading(true);
    try {
      const { data } = await reportsAPI.getReferrals(rankStartDate, rankEndDate);
      setReferralsReport(data);
    } catch (error) {
      logger.error('Error loading referrals report:', error);
      toast.error('No se pudo cargar el reporte de referidos');
    } finally {
      setReferralsLoading(false);
    }
  }, [selectedBusiness, rankStartDate, rankEndDate]);

  const loadPerformance = useCallback(async () => {
    if (!selectedBusiness) return;
    setPerformanceLoading(true);
    try {
      const bid = perfBarberFilter && perfBarberFilter !== 'all' ? perfBarberFilter : null;
      const [perfRes, barbersRes] = await Promise.all([
        reportsAPI.getEmployeePerformance(rankStartDate, rankEndDate, bid),
        bid ? Promise.resolve(null) : barbersAPI.getAll({ include_pos_shared: true }),
      ]);
      setPerformanceReport(perfRes.data);
      if (barbersRes) setPerfBarbers(barbersRes.data || []);
    } catch (error) {
      logger.error('Error loading performance report:', error);
      toast.error('No se pudo cargar el rendimiento por empleado');
    } finally {
      setPerformanceLoading(false);
    }
  }, [selectedBusiness, rankStartDate, rankEndDate, perfBarberFilter]);

  useEffect(() => {
    if (!selectedBusiness) return;
    loadCustomers();
    loadRankings();
    loadReferrals();
    loadPerformance();
  }, [selectedBusiness, loadCustomers, loadRankings, loadReferrals, loadPerformance]);

  const loadTopClients = useCallback(async () => {
    if (!selectedBusiness) return;
    setTopClientsLoading(true);
    try {
      const { data } = await reportsAPI.getTopClients(rankStartDate, rankEndDate, topClientsLimit);
      setTopClientsReport(data);
    } catch (error) {
      logger.error('Error loading top clients report:', error);
      toast.error('No se pudo cargar el reporte Top Clientes');
    } finally {
      setTopClientsLoading(false);
    }
  }, [selectedBusiness, rankStartDate, rankEndDate, topClientsLimit]);

  useEffect(() => {
    if (!selectedBusiness) return;
    loadTopClients();
  }, [selectedBusiness, loadTopClients]);

  const handlePayEarnings = async (barber, pendingComm, pendingTips) => {
    const total = (pendingComm || 0) + (pendingTips || 0);
    if (total <= 0) {
      toast.info('No hay comisiones ni propinas pendientes en este rango');
      return;
    }
    const confirmed = window.confirm(
      `¿Confirmas el pago de nómina a ${barber.display_name}?\n\n` +
      `Período: ${startDate} → ${endDate}\n` +
      `Comisiones pendientes: ${formatCurrency(pendingComm || 0)}\n` +
      `Propinas pendientes:   ${formatCurrency(pendingTips || 0)}\n` +
      `Total a pagar:         ${formatCurrency(total)}\n\n` +
      `Se marcarán como pagadas y se descontarán del ingreso en el Reporte de caja detallado.`
    );
    if (!confirmed) return;
    try {
      setPayingBarberId(barber.id);
      const { data } = await commissionsAPI.payEarnings(barber.id, startDate, endDate);
      toast.success(
        `Nómina pagada a ${barber.display_name}: ${formatCurrency(data.total_paid)} ` +
        `(${data.commissions.count} comis. + ${data.tips.count} propinas)`
      );
      await loadReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo registrar el pago');
    } finally {
      setPayingBarberId(null);
    }
  };

  const handleOpenPayrollHistory = async (barber) => {
    setHistoryBarber(barber);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const { data } = await commissionsAPI.payrollHistory(barber.id, 100);
      setHistoryData(data);
    } catch (error) {
      toast.error('No se pudo cargar el historial');
      setHistoryBarber(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCloseCaja = async () => {
    if (!todayReport || todayReport.summary?.total_transactions === 0) {
      toast.error('No hay ventas abiertas para cerrar');
      return;
    }
    const confirmed = window.confirm(
      `¿Cerrar la caja del día?\n\n` +
      `Total: ${todayReport.summary.total_sales.toLocaleString('es-MX', {style:'currency',currency:'MXN'})}\n` +
      `Transacciones: ${todayReport.summary.total_transactions}\n\n` +
      `Las ventas pasarán al Historial y el corte del día quedará en 0.`
    );
    if (!confirmed) return;
    try {
      setClosingCaja(true);
      await cashCutsAPI.close();
      toast.success('Caja cerrada correctamente');
      await Promise.all([loadDailyReport(), loadCashCuts()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caja');
    } finally {
      setClosingCaja(false);
    }
  };

  const loadMonthlyReport = useCallback(async () => {
    if (!selectedBusiness) return;
    try {
      const now = new Date();
      const tz = { timeZone: 'America/Mexico_City' };
      const y = parseInt(now.toLocaleDateString('en-CA', tz).slice(0, 4), 10);
      const m = parseInt(now.toLocaleDateString('en-CA', tz).slice(5, 7), 10);
      const first = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const last = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const res = await reportsAPI.getSales(first, last);
      setMonthlyReport(res.data);
    } catch (error) {
      logger.error('Error loading monthly report:', error);
    }
  }, [selectedBusiness]);

  const loadReports = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const [salesRes, barbersRes] = await Promise.all([
        reportsAPI.getSales(startDate, endDate),
        reportsAPI.getBarbers(startDate, endDate)
      ]);
      
      setSalesReport(salesRes.data);
      setBarbersReport(barbersRes.data);
    } catch (error) {
      logger.error('Error loading reports:', error);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, startDate, endDate]);

  useEffect(() => {
    loadReports();
    loadDailyReport();
    loadMonthlyReport();
    loadCashCuts();
  }, [loadReports, loadDailyReport, loadMonthlyReport, loadCashCuts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  // Payment methods data: include EVERY method that has at least one transaction.
  // Filter out zero-count rows and assign a stable color per method.
  const paymentData = (salesReport?.by_payment_method || [])
    .filter(p => (p.count || 0) > 0 || (p.total || 0) > 0)
    .map((p, i) => ({
      method: p.method,
      name: paymentMethodLabels[p.method] || p.method,
      value: Number(p.total || 0),
      count: Number(p.count || 0),
      color: PAYMENT_COLORS[p.method] || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reportes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Reportes
          </h1>
          <p className="text-slate-500 mt-1">
            Análisis de {selectedBusiness?.name}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/reportes/caja-detallado"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-900 bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
            data-testid="link-caja-detallado"
          >
            <Wallet className="w-4 h-4" /> Reporte Caja detallado
          </Link>
        </div>
      </div>

      {/* Date Range */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-slate-500">Desde</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40 bg-slate-100 border-slate-200"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-slate-500">Hasta</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40 bg-slate-100 border-slate-200"
              />
            </div>
            <Button onClick={loadReports} variant="outline" className="border-slate-200">
              Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-slate-100">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ventas totales</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {formatCurrency(salesReport?.summary?.total_sales || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-slate-100">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Transacciones</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {salesReport?.summary?.total_transactions || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-slate-100">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ticket promedio</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {formatCurrency(salesReport?.summary?.average_ticket || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-slate-100">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Barberos activos</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {barbersReport.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="daily">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="daily" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-daily">
            <Receipt className="w-4 h-4 mr-2" />
            Corte del día
          </TabsTrigger>
          <TabsTrigger value="monthly" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-monthly">
            <CalendarDays className="w-4 h-4 mr-2" />
            Mensual
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-history">
            <BarChart3 className="w-4 h-4 mr-2" />
            Historial de cortes
          </TabsTrigger>
          <TabsTrigger value="transacciones" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-transacciones">
            <DollarSign className="w-4 h-4 mr-2" />
            Transacciones
          </TabsTrigger>
          <TabsTrigger value="barbers" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <Users className="w-4 h-4 mr-2" />
            Barberos
          </TabsTrigger>
          <TabsTrigger value="clientes" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-clientes">
            <UserCheck className="w-4 h-4 mr-2" />
            Clientes
          </TabsTrigger>
          <TabsTrigger value="rankings" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-rankings">
            <Trophy className="w-4 h-4 mr-2" />
            Rankings
          </TabsTrigger>
          <TabsTrigger value="referidos" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-referidos">
            <Sparkles className="w-4 h-4 mr-2" />
            Referidos
          </TabsTrigger>
          <TabsTrigger value="rendimiento" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-rendimiento">
            <BarChart3 className="w-4 h-4 mr-2" />
            Rendimiento
          </TabsTrigger>
          <TabsTrigger value="top-clientes" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-top-clientes">
            <Trophy className="w-4 h-4 mr-2" />
            Top Clientes
          </TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-4 space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Caja del día abierta. Al cerrar, las ventas pasan al historial y la pantalla queda en 0.
            </p>
            <Button
              onClick={handleCloseCaja}
              disabled={closingCaja || !todayReport || todayReport.summary?.total_transactions === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="close-caja-btn"
            >
              {closingCaja ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Cerrar caja
            </Button>
          </div>
          <CashCutCard
            title="Corte del día"
            description={`Ingresos abiertos de hoy (${formatDate(new Date())}) por método de pago.`}
            report={todayReport}
            testId="report-daily"
          />
        </TabsContent>

        <TabsContent value="monthly" className="mt-4 space-y-6">
          <CashCutCard
            title="Reporte mensual"
            description={`Ingresos acumulados del mes en curso, totalizados por método de pago.`}
            report={monthlyReport}
            testId="report-monthly"
          />
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-4" data-testid="history-content">
          {cashCuts.length === 0 ? (
            <Card className="border-slate-200 bg-white">
              <CardContent className="py-12 text-center text-slate-400">
                Aún no has cerrado ninguna caja. Cuando lo hagas desde la pestaña &ldquo;Corte del día&rdquo;,
                aparecerán aquí los cortes históricos.
              </CardContent>
            </Card>
          ) : (
            cashCuts.map((cut) => {
              // Convert backend dict by_payment_method {method: {total,count}} to array shape that CashCutCard expects
              const cutForCard = {
                summary: {
                  total_sales: cut.total_sales,
                  total_transactions: cut.total_transactions,
                  average_ticket: cut.total_transactions ? cut.total_sales / cut.total_transactions : 0,
                },
                by_payment_method: Object.entries(cut.by_payment_method || {}).map(([method, v]) => ({
                  method,
                  total: v.total || 0,
                  count: v.count || 0,
                })),
              };
              return (
                <CashCutCard
                  key={cut.id}
                  title={`Corte ${cut.date}`}
                  description={`Cerrado ${cut.closed_by_name ? `por ${cut.closed_by_name} ` : ''}el ${new Date(cut.closed_at).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}`}
                  report={cutForCard}
                  testId={`history-cut-${cut.id}`}
                />
              );
            })
          )}
        </TabsContent>

        <TabsContent value="transacciones" className="mt-4 space-y-4">
          {/* Filters: preset ranges + date inputs + type filter */}
          <Card className="border-slate-200 bg-white">
            <CardHeader className="pb-3">
              <CardTitle style={{ fontFamily: 'Manrope' }} className="text-base">
                Transacciones
              </CardTitle>
              <CardDescription>
                Ventas del POS, gastos y retiros de caja unificados en una sola vista
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'day', label: 'Hoy' },
                  { key: 'week', label: 'Semana' },
                  { key: 'month', label: 'Mes' },
                  { key: 'custom', label: 'Personalizado' },
                ].map((p) => (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={txRange === p.key ? 'default' : 'outline'}
                    onClick={() => applyTxPreset(p.key)}
                    data-testid={`tx-preset-${p.key}`}
                    className={txRange === p.key ? 'bg-slate-900 text-white hover:bg-slate-800' : ''}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Desde</label>
                  <Input
                    type="date"
                    value={txStart}
                    onChange={(e) => { setTxStart(e.target.value); setTxRange('custom'); }}
                    data-testid="tx-start-date"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Hasta</label>
                  <Input
                    type="date"
                    value={txEnd}
                    onChange={(e) => { setTxEnd(e.target.value); setTxRange('custom'); }}
                    data-testid="tx-end-date"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Filtrar por tipo</label>
                  <div className="flex gap-1">
                    {[
                      { key: 'all', label: 'Todas' },
                      { key: 'sale', label: 'Ventas' },
                      { key: 'expense', label: 'Gastos' },
                      { key: 'withdrawal', label: 'Retiros' },
                    ].map((f) => (
                      <Button
                        key={f.key}
                        size="sm"
                        variant={txFilter === f.key ? 'default' : 'outline'}
                        onClick={() => setTxFilter(f.key)}
                        data-testid={`tx-filter-${f.key}`}
                        className={txFilter === f.key ? 'bg-slate-900 text-white hover:bg-slate-800 flex-1' : 'flex-1'}
                      >
                        {f.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary strip: totals over the current filter view */}
          {(() => {
            const filtered = txFilter === 'all' ? transactions : transactions.filter((t) => t.kind === txFilter);
            const inflow = filtered.filter((t) => t.kind === 'sale' && !t.cancelled).reduce((a, b) => a + b.amount, 0);
            const outflow = filtered.filter((t) => t.kind !== 'sale').reduce((a, b) => a + b.amount, 0);
            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-emerald-700 mb-1">Ingresos (ventas)</p>
                    <p className="text-2xl font-bold text-emerald-900" data-testid="tx-total-in">{formatCurrency(inflow)}</p>
                  </CardContent>
                </Card>
                <Card className="border-rose-200 bg-rose-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-rose-700 mb-1">Salidas (gastos + retiros)</p>
                    <p className="text-2xl font-bold text-rose-900" data-testid="tx-total-out">{formatCurrency(outflow)}</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 bg-white">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 mb-1">Neto</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid="tx-total-net">{formatCurrency(inflow - outflow)}</p>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Transactions table */}
          <Card className="border-slate-200 bg-white">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope' }} className="text-base">
                  Detalle de transacciones
                </CardTitle>
                <CardDescription>
                  {txLoading
                    ? 'Cargando…'
                    : `${(txFilter === 'all' ? transactions : transactions.filter((t) => t.kind === txFilter)).length} movimiento(s) en el rango`}
                </CardDescription>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const rows = (txFilter === 'all' ? transactions : transactions.filter((t) => t.kind === txFilter))
                    .map((t) => ({
                      fecha: formatDateTime(t.ts),
                      tipo: t.kind === 'sale' ? 'Venta' : t.kind === 'expense' ? 'Gasto' : 'Retiro',
                      concepto: t.concept,
                      metodo: t.method,
                      monto: (t.sign > 0 ? '' : '-') + Number(t.amount || 0).toFixed(2),
                    }));
                  exportToExcel(`Transacciones_${txStart}_a_${txEnd}`, [{
                    name: 'Transacciones',
                    columns: [
                      { header: 'Fecha', key: 'fecha' },
                      { header: 'Tipo', key: 'tipo' },
                      { header: 'Concepto', key: 'concepto' },
                      { header: 'Método', key: 'metodo' },
                      { header: 'Monto', key: 'monto' },
                    ],
                    rows,
                  }]);
                }}
                disabled={txLoading || !transactions.length}
                data-testid="tx-export-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exportar Excel
              </Button>
            </CardHeader>
            <CardContent>
              {txLoading ? (
                <div className="py-12 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (txFilter === 'all' ? transactions : transactions.filter((t) => t.kind === txFilter)).length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  Sin movimientos en el rango seleccionado
                </div>
              ) : (
                <div className="max-h-[65vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-40">Fecha</TableHead>
                        <TableHead className="w-24">Tipo</TableHead>
                        <TableHead>Concepto</TableHead>
                        <TableHead className="w-24">Método</TableHead>
                        <TableHead className="text-right w-28">Monto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="tx-table-body">
                      {(txFilter === 'all' ? transactions : transactions.filter((t) => t.kind === txFilter)).map((t) => {
                        const cfg = t.kind === 'sale'
                          ? { icon: ArrowUpCircle, color: 'text-emerald-600', label: 'Venta' }
                          : t.kind === 'expense'
                            ? { icon: ArrowDownCircle, color: 'text-rose-600', label: 'Gasto' }
                            : { icon: MinusCircle, color: 'text-amber-600', label: 'Retiro' };
                        const Icon = cfg.icon;
                        return (
                          <TableRow key={t.id} className={t.cancelled ? 'opacity-50 line-through' : ''}>
                            <TableCell className="text-slate-600 whitespace-nowrap">{formatDateTime(t.ts)}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center gap-1 text-xs font-medium ${cfg.color}`}>
                                <Icon className="w-3.5 h-3.5" /> {cfg.label}
                              </span>
                            </TableCell>
                            <TableCell className="max-w-md truncate">{t.concept}</TableCell>
                            <TableCell className="text-slate-500 uppercase text-xs">
                              {t.kind === 'sale' && !t.cancelled && isAdmin() ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1 hover:bg-slate-100 rounded-full transition-colors px-1.5 py-0.5"
                                      data-testid={`tx-payment-trigger-${t.saleId}`}
                                      title="Cambiar método de pago"
                                    >
                                      <span>{paymentMethodLabels[t.method] || t.method}</span>
                                      <span className="text-[10px] text-slate-400">▾</span>
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuLabel className="text-xs">Cambiar método</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {['cash', 'card', 'transfer', 'mixed'].map((key) => (
                                      <DropdownMenuItem
                                        key={key}
                                        onClick={() => handleTxChangePaymentMethod(t, key)}
                                        className={cn(
                                          'cursor-pointer',
                                          t.method === key && 'bg-slate-100 font-semibold'
                                        )}
                                        data-testid={`tx-payment-option-${t.saleId}-${key}`}
                                      >
                                        {paymentMethodLabels[key] || key}
                                        {t.method === key && (
                                          <span className="ml-auto text-xs text-emerald-600">●</span>
                                        )}
                                      </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuSeparator />
                                    {canEditTotal && !t.pendingTotal && (
                                      <DropdownMenuItem
                                        onClick={() => openEditTotal(t.sale)}
                                        className="cursor-pointer text-slate-700"
                                        data-testid={`tx-edit-total-${t.saleId}`}
                                      >
                                        <DollarSign className="w-3.5 h-3.5 mr-2" />
                                        Editar monto…
                                      </DropdownMenuItem>
                                    )}
                                    {canEditTotal && !t.cancelled && !t.pendingCancel && !t.pendingTotal && (
                                      <DropdownMenuItem
                                        onClick={() => openCancelSale(t.sale)}
                                        className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                                        data-testid={`tx-cancel-sale-${t.saleId}`}
                                      >
                                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                                        Eliminar venta…
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem
                                      onClick={() => openAuditLog(t.sale)}
                                      className="cursor-pointer text-slate-600"
                                      data-testid={`tx-audit-log-${t.saleId}`}
                                    >
                                      <ScrollText className="w-3.5 h-3.5 mr-2" />
                                      Ver auditoría
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <span>{paymentMethodLabels[t.method] || t.method}</span>
                              )}
                            </TableCell>
                            <TableCell className={`text-right tabular-nums font-medium ${t.sign > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {t.sign > 0 ? '+' : '-'}{formatCurrency(t.amount)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="barbers" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {barbersReport.map((data, index) => (
              <Card 
                key={data.barber?.id || index} 
                className="border-slate-200 bg-white animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-slate-200 text-white">
                        {data.barber?.display_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-bold">{data.barber?.display_name || 'Desconocido'}</h3>
                      <p className="text-sm text-slate-500">
                        {data.barber?.specialties?.join(', ') || 'Sin especialidad'}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ventas</span>
                      <span className="font-bold">{formatCurrency(data.total_sales)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Transacciones</span>
                      <span className="font-medium">{data.transactions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Citas completadas</span>
                      <span className="font-medium">{data.completed_appointments}/{data.total_appointments}</span>
                    </div>
                    <div className="pt-2 border-t border-slate-200">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Comisiones</span>
                        <span className="font-bold text-amber-400">{formatCurrency(data.total_commissions)}</span>
                      </div>
                      {data.pending_commissions > 0 && (
                        <p className="text-xs text-amber-400/70 text-right mt-1">
                          {formatCurrency(data.pending_commissions)} pendiente
                        </p>
                      )}
                    </div>
                    <div className="pt-2 border-t border-slate-200">
                      <div className="flex justify-between">
                        <span className="text-slate-500 flex items-center gap-1">
                          Propinas
                        </span>
                        <span
                          className="font-bold text-emerald-600"
                          data-testid={`barber-tips-${data.barber.id}`}
                        >
                          {formatCurrency(data.total_tips || 0)}
                        </span>
                      </div>
                      {(data.pending_tips || 0) > 0 && (
                        <p className="text-xs text-emerald-500/80 text-right mt-1">
                          {formatCurrency(data.pending_tips)} pendiente
                        </p>
                      )}
                      <p className="text-[10px] text-slate-400 text-right mt-1">
                        100% para el barbero
                      </p>
                    </div>

                    {/* Nómina — botón para marcar comisiones+propinas como pagadas */}
                    <div className="pt-3 border-t border-slate-200">
                      {(() => {
                        const pendingComm = Number(data.pending_commissions || 0);
                        const pendingTips = Number(data.pending_tips || 0);
                        const pendingTotal = pendingComm + pendingTips;
                        const isPaying = payingBarberId === data.barber?.id;
                        return (
                          <>
                            <div className="flex justify-between mb-2">
                              <span className="text-slate-600 font-medium">Nómina pendiente</span>
                              <span
                                className={cn(
                                  'font-bold',
                                  pendingTotal > 0 ? 'text-slate-900' : 'text-slate-400'
                                )}
                                data-testid={`barber-payroll-pending-${data.barber.id}`}
                              >
                                {formatCurrency(pendingTotal)}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                              disabled={pendingTotal <= 0 || isPaying}
                              onClick={() =>
                                handlePayEarnings(data.barber, pendingComm, pendingTips)
                              }
                              data-testid={`pay-earnings-btn-${data.barber.id}`}
                            >
                              {isPaying ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <BadgeDollarSign className="w-4 h-4 mr-2" />
                              )}
                              {pendingTotal > 0
                                ? `Pagar nómina — ${formatCurrency(pendingTotal)}`
                                : 'Sin pendientes'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full mt-2 border-slate-200 text-slate-600 hover:bg-slate-50"
                              onClick={() => handleOpenPayrollHistory(data.barber)}
                              data-testid={`payroll-history-btn-${data.barber.id}`}
                            >
                              <History className="w-4 h-4 mr-2" />
                              Historial de pagos
                            </Button>
                            <p className="text-[10px] text-slate-400 text-center mt-1">
                              Marca comisiones y propinas como pagadas en {startDate} → {endDate}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {barbersReport.length === 0 && (
            <Card className="border-slate-200 bg-white">
              <CardContent className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Users className="w-12 h-12 mb-3 opacity-50" />
                <p>No hay datos de barberos para este período</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===================== CLIENTES TAB ===================== */}
        <TabsContent value="clientes" className="mt-4 space-y-4" data-testid="clientes-content">
          <RankFilters
            preset={rankPreset}
            startDate={rankStartDate}
            endDate={rankEndDate}
            onPreset={handleRankPreset}
            onStart={(v) => { setRankStartDate(v); setRankPreset('custom'); }}
            onEnd={(v) => { setRankEndDate(v); setRankPreset('custom'); }}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <UserCheck className="w-4 h-4" /> Con cita
                </div>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Manrope' }} data-testid="stat-booked-count">
                  {customersReport?.walk_in_stats?.booked ?? 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {customersReport?.walk_in_stats?.booked_pct ?? 0}% del total • {formatCurrency(customersReport?.walk_in_stats?.booked_amount || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <Sparkles className="w-4 h-4" /> Sin cita (walk-in)
                </div>
                <p className="text-3xl font-bold mt-2 text-amber-600" style={{ fontFamily: 'Manrope' }} data-testid="stat-walkin-count">
                  {customersReport?.walk_in_stats?.walk_ins ?? 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {customersReport?.walk_in_stats?.walk_in_pct ?? 0}% del total • {formatCurrency(customersReport?.walk_in_stats?.walk_in_amount || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <Receipt className="w-4 h-4" /> Total transacciones
                </div>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Manrope' }} data-testid="stat-total-tx">
                  {customersReport?.walk_in_stats?.total ?? 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {rankStartDate} → {rankEndDate}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                Top 10 clientes frecuentes
              </CardTitle>
              <CardDescription>Ordenados por consumo total en el rango de fechas seleccionado.</CardDescription>
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  disabled={!customersReport?.top_clients?.length}
                  onClick={() => {
                    const rows = (customersReport?.top_clients || []).map((c, i) => ({
                      pos: i + 1,
                      cliente: c.client_name,
                      telefono: c.phone || '',
                      visitas: c.visits,
                      total: Number(c.total_spent || 0).toFixed(2),
                    }));
                    exportToExcel(`Top_Clientes_${rankStartDate}_a_${rankEndDate}`, [{
                      name: 'Top clientes',
                      columns: [
                        { header: '#', key: 'pos' },
                        { header: 'Cliente', key: 'cliente' },
                        { header: 'Teléfono', key: 'telefono' },
                        { header: 'Visitas', key: 'visitas' },
                        { header: 'Total gastado', key: 'total' },
                      ],
                      rows,
                    }]);
                    toast.success('Excel descargado');
                  }}
                  data-testid="export-top-clients-excel"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
                </Button>
                <Button size="sm" variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  disabled={!customersReport?.top_clients?.length}
                  onClick={() => {
                    const rows = (customersReport?.top_clients || []).map((c, i) => ({
                      pos: String(i + 1), cliente: c.client_name, telefono: c.phone || '',
                      visitas: String(c.visits), total: Number(c.total_spent || 0).toFixed(2),
                    }));
                    exportToPDF(`Top_Clientes_${rankStartDate}_a_${rankEndDate}`, {
                      businessName: selectedBusiness?.name,
                      title: 'Top clientes frecuentes',
                      subtitle: `Período: ${rankStartDate} → ${rankEndDate}`,
                      sections: [{
                        title: 'Ranking',
                        columns: [
                          { header: '#', key: 'pos' },
                          { header: 'Cliente', key: 'cliente' },
                          { header: 'Teléfono', key: 'telefono' },
                          { header: 'Visitas', key: 'visitas' },
                          { header: 'Total', key: 'total' },
                        ],
                        rows,
                      }],
                    });
                    toast.success('PDF descargado');
                  }}
                  data-testid="export-top-clients-pdf"
                >
                  <FileText className="w-3 h-3 mr-1" /> PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {customersLoading ? (
                <div className="flex items-center justify-center h-32 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
                </div>
              ) : (customersReport?.top_clients?.length || 0) === 0 ? (
                <div className="text-slate-500 text-sm py-8 text-center" data-testid="empty-top-clients">
                  Sin datos de clientes en este período
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="text-left py-2 w-10">#</th>
                        <th className="text-left py-2">Cliente</th>
                        <th className="text-left py-2">Teléfono</th>
                        <th className="text-right py-2">Visitas</th>
                        <th className="text-right py-2">Total gastado</th>
                        <th className="text-right py-2">Última visita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(customersReport?.top_clients || []).map((c, idx) => (
                        <tr key={c.client_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`top-client-row-${idx}`}>
                          <td className="py-2 text-slate-400 font-mono">{idx + 1}</td>
                          <td className="py-2 font-medium">{c.client_name}</td>
                          <td className="py-2 text-slate-500 text-xs">{c.phone || '—'}</td>
                          <td className="py-2 text-right">{c.visits}</td>
                          <td className="py-2 text-right font-bold text-emerald-600">
                            {formatCurrency(c.total_spent)}
                          </td>
                          <td className="py-2 text-right text-xs text-slate-500">
                            {c.last_visit ? formatDate(c.last_visit) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===================== RANKINGS TAB ===================== */}
        <TabsContent value="rankings" className="mt-4 space-y-4" data-testid="rankings-content">
          <RankFilters
            preset={rankPreset}
            startDate={rankStartDate}
            endDate={rankEndDate}
            onPreset={handleRankPreset}
            onStart={(v) => { setRankStartDate(v); setRankPreset('custom'); }}
            onEnd={(v) => { setRankEndDate(v); setRankPreset('custom'); }}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200 bg-white">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Scissors className="w-4 h-4" /> Top 10 servicios
                </CardTitle>
                <CardDescription>Ordenados por ingreso generado.</CardDescription>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline"
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    disabled={!rankingsReport?.top_services?.length}
                    onClick={() => {
                      const rows = (rankingsReport?.top_services || []).map((x, i) => ({
                        pos: i + 1, nombre: x.name,
                        ventas: x.quantity, ingreso: Number(x.revenue || 0).toFixed(2),
                      }));
                      exportToExcel(`Top_Servicios_${rankStartDate}_a_${rankEndDate}`, [{
                        name: 'Top servicios',
                        columns: [
                          { header: '#', key: 'pos' }, { header: 'Servicio', key: 'nombre' },
                          { header: 'Ventas', key: 'ventas' }, { header: 'Ingreso', key: 'ingreso' },
                        ], rows,
                      }]);
                      toast.success('Excel descargado');
                    }}
                    data-testid="export-services-excel"
                  ><FileSpreadsheet className="w-3 h-3 mr-1" /> Excel</Button>
                  <Button size="sm" variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    disabled={!rankingsReport?.top_services?.length}
                    onClick={() => {
                      const rows = (rankingsReport?.top_services || []).map((x, i) => ({
                        pos: String(i + 1), nombre: x.name,
                        ventas: String(x.quantity), ingreso: Number(x.revenue || 0).toFixed(2),
                      }));
                      exportToPDF(`Top_Servicios_${rankStartDate}_a_${rankEndDate}`, {
                        businessName: selectedBusiness?.name,
                        title: 'Top servicios',
                        subtitle: `Período: ${rankStartDate} → ${rankEndDate}`,
                        sections: [{
                          title: 'Ranking',
                          columns: [
                            { header: '#', key: 'pos' }, { header: 'Servicio', key: 'nombre' },
                            { header: 'Ventas', key: 'ventas' }, { header: 'Ingreso', key: 'ingreso' },
                          ], rows,
                        }],
                      });
                      toast.success('PDF descargado');
                    }}
                    data-testid="export-services-pdf"
                  ><FileText className="w-3 h-3 mr-1" /> PDF</Button>
                </div>
              </CardHeader>
              <CardContent>
                {rankingsLoading ? (
                  <div className="flex items-center justify-center h-32 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
                  </div>
                ) : (rankingsReport?.top_services?.length || 0) === 0 ? (
                  <div className="text-slate-500 text-sm py-8 text-center" data-testid="empty-top-services">
                    Sin ventas de servicios en este período
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="text-left py-2 w-10">#</th>
                        <th className="text-left py-2">Servicio</th>
                        <th className="text-right py-2">Ventas</th>
                        <th className="text-right py-2">Ingreso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rankingsReport?.top_services || []).map((s, idx) => (
                        <tr key={s.item_id || s.name} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`top-service-row-${idx}`}>
                          <td className="py-2 text-slate-400 font-mono">{idx + 1}</td>
                          <td className="py-2 font-medium">{s.name}</td>
                          <td className="py-2 text-right">{s.quantity}</td>
                          <td className="py-2 text-right font-bold text-emerald-600">{formatCurrency(s.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Package className="w-4 h-4" /> Top 10 productos
                </CardTitle>
                <CardDescription>Ordenados por ingreso generado.</CardDescription>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline"
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    disabled={!rankingsReport?.top_products?.length}
                    onClick={() => {
                      const rows = (rankingsReport?.top_products || []).map((x, i) => ({
                        pos: i + 1, nombre: x.name,
                        unidades: x.quantity, ingreso: Number(x.revenue || 0).toFixed(2),
                      }));
                      exportToExcel(`Top_Productos_${rankStartDate}_a_${rankEndDate}`, [{
                        name: 'Top productos',
                        columns: [
                          { header: '#', key: 'pos' }, { header: 'Producto', key: 'nombre' },
                          { header: 'Unidades', key: 'unidades' }, { header: 'Ingreso', key: 'ingreso' },
                        ], rows,
                      }]);
                      toast.success('Excel descargado');
                    }}
                    data-testid="export-products-excel"
                  ><FileSpreadsheet className="w-3 h-3 mr-1" /> Excel</Button>
                  <Button size="sm" variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    disabled={!rankingsReport?.top_products?.length}
                    onClick={() => {
                      const rows = (rankingsReport?.top_products || []).map((x, i) => ({
                        pos: String(i + 1), nombre: x.name,
                        unidades: String(x.quantity), ingreso: Number(x.revenue || 0).toFixed(2),
                      }));
                      exportToPDF(`Top_Productos_${rankStartDate}_a_${rankEndDate}`, {
                        businessName: selectedBusiness?.name,
                        title: 'Top productos',
                        subtitle: `Período: ${rankStartDate} → ${rankEndDate}`,
                        sections: [{
                          title: 'Ranking',
                          columns: [
                            { header: '#', key: 'pos' }, { header: 'Producto', key: 'nombre' },
                            { header: 'Unidades', key: 'unidades' }, { header: 'Ingreso', key: 'ingreso' },
                          ], rows,
                        }],
                      });
                      toast.success('PDF descargado');
                    }}
                    data-testid="export-products-pdf"
                  ><FileText className="w-3 h-3 mr-1" /> PDF</Button>
                </div>
              </CardHeader>
              <CardContent>
                {rankingsLoading ? (
                  <div className="flex items-center justify-center h-32 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
                  </div>
                ) : (rankingsReport?.top_products?.length || 0) === 0 ? (
                  <div className="text-slate-500 text-sm py-8 text-center" data-testid="empty-top-products">
                    Sin ventas de productos en este período
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="text-left py-2 w-10">#</th>
                        <th className="text-left py-2">Producto</th>
                        <th className="text-right py-2">Unidades</th>
                        <th className="text-right py-2">Ingreso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rankingsReport?.top_products || []).map((p, idx) => (
                        <tr key={p.item_id || p.name} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`top-product-row-${idx}`}>
                          <td className="py-2 text-slate-400 font-mono">{idx + 1}</td>
                          <td className="py-2 font-medium">{p.name}</td>
                          <td className="py-2 text-right">{p.quantity}</td>
                          <td className="py-2 text-right font-bold text-emerald-600">{formatCurrency(p.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===================== REFERIDOS TAB ===================== */}
        <TabsContent value="referidos" className="mt-4 space-y-4" data-testid="referidos-content">
          <RankFilters
            preset={rankPreset}
            startDate={rankStartDate}
            endDate={rankEndDate}
            onPreset={handleRankPreset}
            onStart={(v) => { setRankStartDate(v); setRankPreset('custom'); }}
            onEnd={(v) => { setRankEndDate(v); setRankPreset('custom'); }}
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="referrals-kpis">
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <Users className="w-4 h-4" /> Referidores
                </div>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Manrope' }} data-testid="referrals-kpi-referrers">
                  {referralsReport?.totals?.referrers ?? 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">clientes que compartieron su liga</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <UserCheck className="w-4 h-4" /> Registros
                </div>
                <p className="text-3xl font-bold mt-2 text-sky-600" style={{ fontFamily: 'Manrope' }} data-testid="referrals-kpi-signups">
                  {referralsReport?.totals?.signups ?? 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">personas inscritas mediante referido</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <Scissors className="w-4 h-4" /> Atendidos
                </div>
                <p className="text-3xl font-bold mt-2 text-emerald-600" style={{ fontFamily: 'Manrope' }} data-testid="referrals-kpi-attended">
                  {referralsReport?.totals?.attended ?? 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {referralsReport?.totals?.signups
                    ? `${Math.round((referralsReport.totals.attended / referralsReport.totals.signups) * 100)}% de conversión`
                    : 'referidos con al menos 1 visita'}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200 bg-white">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Sparkles className="w-4 h-4" /> Programa de referidos
                </CardTitle>
                <CardDescription>
                  {rankStartDate} → {rankEndDate} · Sucursal: {selectedBusiness?.name || '—'}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  disabled={!referralsReport?.referrers?.length}
                  onClick={() => {
                    const rows = [];
                    (referralsReport?.referrers || []).forEach((g) => {
                      (g.referrals || []).forEach((r) => {
                        rows.push({
                          referidor: g.referrer_name,
                          telefono_ref: g.referrer_phone || '—',
                          referido: r.referred_name,
                          telefono: r.referred_phone || '—',
                          registro: r.signed_up_at ? formatDate(r.signed_up_at) : '—',
                          asistio: r.attended ? 'Sí' : 'No',
                          fecha_visita: r.attended_at ? formatDate(r.attended_at) : '—',
                          atendido_por: r.barber_name || '—',
                        });
                      });
                    });
                    exportToExcel(`Reporte_Referidos_${rankStartDate}_a_${rankEndDate}`, [{
                      name: 'Referidos',
                      columns: [
                        { header: 'Referidor', key: 'referidor' },
                        { header: 'Tel. referidor', key: 'telefono_ref' },
                        { header: 'Referido', key: 'referido' },
                        { header: 'Tel. referido', key: 'telefono' },
                        { header: 'Fecha de registro', key: 'registro' },
                        { header: '¿Asistió?', key: 'asistio' },
                        { header: 'Fecha de visita', key: 'fecha_visita' },
                        { header: 'Atendido por', key: 'atendido_por' },
                      ],
                      rows,
                    }]);
                    toast.success('Excel descargado');
                  }}
                  data-testid="export-referrals-excel"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
                </Button>
                <Button size="sm" variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  disabled={!referralsReport?.referrers?.length}
                  onClick={() => {
                    const rows = [];
                    (referralsReport?.referrers || []).forEach((g) => {
                      (g.referrals || []).forEach((r) => {
                        rows.push({
                          referidor: g.referrer_name,
                          referido: r.referred_name,
                          registro: r.signed_up_at ? formatDate(r.signed_up_at) : '—',
                          asistio: r.attended ? 'Sí' : 'No',
                          atendido_por: r.barber_name || '—',
                        });
                      });
                    });
                    exportToPDF(`Reporte_Referidos_${rankStartDate}_a_${rankEndDate}`, {
                      businessName: selectedBusiness?.name,
                      title: 'Reporte de Referidos',
                      subtitle: `Período: ${rankStartDate} → ${rankEndDate}`,
                      sections: [{
                        title: 'Programa de referidos',
                        columns: [
                          { header: 'Referidor', key: 'referidor' },
                          { header: 'Referido', key: 'referido' },
                          { header: 'Registro', key: 'registro' },
                          { header: '¿Asistió?', key: 'asistio' },
                          { header: 'Atendido por', key: 'atendido_por' },
                        ],
                        rows,
                      }],
                    });
                    toast.success('PDF descargado');
                  }}
                  data-testid="export-referrals-pdf"
                >
                  <FileText className="w-3 h-3 mr-1" /> PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {referralsLoading ? (
                <div className="py-16 flex items-center justify-center text-slate-500">
                  <Loader2 className="w-6 h-6 mr-2 animate-spin" /> Cargando reporte…
                </div>
              ) : !referralsReport?.referrers?.length ? (
                <div className="py-16 text-center text-slate-500" data-testid="referrals-empty">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>Sin referidos registrados en este período.</p>
                  <p className="text-xs mt-1">Los referidos se contarán cuando un cliente use la liga de otro.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="data-table" data-testid="referrals-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Referidor</TableHead>
                        <TableHead>Referido</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Fecha de registro</TableHead>
                        <TableHead className="text-center">¿Asistió?</TableHead>
                        <TableHead>Atendido por</TableHead>
                        <TableHead>Fecha de visita</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {referralsReport.referrers.map((g) => (
                        g.referrals.map((r, idx) => (
                          <TableRow key={r.referred_id} data-testid={`referral-row-${r.referred_id}`}>
                            {idx === 0 ? (
                              <TableCell
                                rowSpan={g.referrals.length}
                                className="align-top font-medium text-slate-800 bg-slate-50/50 border-r border-slate-100"
                              >
                                <div>{g.referrer_name}</div>
                                <div className="text-[11px] text-slate-500">
                                  {g.signups_count} referido{g.signups_count === 1 ? '' : 's'} · {g.attended_count} asistió
                                </div>
                                {g.referrer_phone && (
                                  <div className="text-[11px] text-slate-400 mt-0.5">{g.referrer_phone}</div>
                                )}
                              </TableCell>
                            ) : null}
                            <TableCell>{r.referred_name}</TableCell>
                            <TableCell className="text-slate-500 text-sm">{r.referred_phone || '—'}</TableCell>
                            <TableCell className="text-slate-500 text-sm">
                              {r.signed_up_at ? formatDate(r.signed_up_at) : '—'}
                            </TableCell>
                            <TableCell className="text-center">
                              {r.attended ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Sí</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">Pendiente</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-700 text-sm">{r.barber_name || '—'}</TableCell>
                            <TableCell className="text-slate-500 text-sm">
                              {r.attended_at ? formatDate(r.attended_at) : '—'}
                            </TableCell>
                          </TableRow>
                        ))
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===================== RENDIMIENTO POR EMPLEADO TAB ===================== */}
        <TabsContent value="rendimiento" className="mt-4 space-y-4" data-testid="rendimiento-content">
          <RankFilters
            preset={rankPreset}
            startDate={rankStartDate}
            endDate={rankEndDate}
            onPreset={handleRankPreset}
            onStart={(v) => { setRankStartDate(v); setRankPreset('custom'); }}
            onEnd={(v) => { setRankEndDate(v); setRankPreset('custom'); }}
          />

          <Card className="border-slate-200 bg-white">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <Label className="text-sm text-slate-600">Empleado:</Label>
              <div className="flex-1 max-w-sm">
                <Select value={perfBarberFilter} onValueChange={setPerfBarberFilter}>
                  <SelectTrigger data-testid="perf-barber-filter" className="bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los empleados</SelectItem>
                    {perfBarbers.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  disabled={!performanceReport?.employees?.length}
                  onClick={() => {
                    const rows = (performanceReport?.employees || []).map((e, i) => ({
                      pos: i + 1,
                      empleado: e.barber_name,
                      clientes: e.clients_count,
                      servicios: e.services_count,
                      productos: e.products_count,
                      total_servicios: Number(e.services_total).toFixed(2),
                      total_productos: Number(e.products_total).toFixed(2),
                      total: Number(e.total_value).toFixed(2),
                    }));
                    exportToExcel(`Rendimiento_${rankStartDate}_a_${rankEndDate}`, [{
                      name: 'Rendimiento',
                      columns: [
                        { header: '#', key: 'pos' },
                        { header: 'Empleado', key: 'empleado' },
                        { header: 'Clientes', key: 'clientes' },
                        { header: 'Servicios', key: 'servicios' },
                        { header: 'Productos', key: 'productos' },
                        { header: 'Total servicios', key: 'total_servicios' },
                        { header: 'Total productos', key: 'total_productos' },
                        { header: 'Total', key: 'total' },
                      ],
                      rows,
                    }]);
                    toast.success('Excel descargado');
                  }}
                  data-testid="export-perf-excel"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
                </Button>
                <Button size="sm" variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  disabled={!performanceReport?.employees?.length}
                  onClick={() => {
                    const rows = (performanceReport?.employees || []).map((e, i) => ({
                      pos: String(i + 1),
                      empleado: e.barber_name,
                      clientes: String(e.clients_count),
                      servicios: String(e.services_count),
                      productos: String(e.products_count),
                      total: Number(e.total_value).toFixed(2),
                    }));
                    exportToPDF(`Rendimiento_${rankStartDate}_a_${rankEndDate}`, {
                      businessName: selectedBusiness?.name,
                      title: 'Rendimiento por Empleado',
                      subtitle: `Período: ${rankStartDate} → ${rankEndDate}`,
                      sections: [{
                        title: 'Ranking',
                        columns: [
                          { header: '#', key: 'pos' },
                          { header: 'Empleado', key: 'empleado' },
                          { header: 'Clientes', key: 'clientes' },
                          { header: 'Servicios', key: 'servicios' },
                          { header: 'Productos', key: 'productos' },
                          { header: 'Total', key: 'total' },
                        ],
                        rows,
                      }],
                    });
                    toast.success('PDF descargado');
                  }}
                  data-testid="export-perf-pdf"
                >
                  <FileText className="w-3 h-3 mr-1" /> PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="perf-kpis">
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Clientes</div>
              <div className="text-2xl font-bold mt-1" data-testid="perf-kpi-clients">{performanceReport?.totals?.clients ?? 0}</div>
            </CardContent></Card>
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Servicios</div>
              <div className="text-2xl font-bold mt-1 text-sky-600" data-testid="perf-kpi-services">{performanceReport?.totals?.services ?? 0}</div>
            </CardContent></Card>
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Productos</div>
              <div className="text-2xl font-bold mt-1 text-amber-600" data-testid="perf-kpi-products">{performanceReport?.totals?.products ?? 0}</div>
            </CardContent></Card>
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Inscritos al Club</div>
              <div className="text-2xl font-bold mt-1 text-rose-600" data-testid="perf-kpi-loyalty">{performanceReport?.totals?.loyalty_enrolled ?? 0}</div>
            </CardContent></Card>
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Total generado</div>
              <div className="text-2xl font-bold mt-1 text-emerald-600" data-testid="perf-kpi-total">{formatCurrency(performanceReport?.totals?.total_value ?? 0)}</div>
            </CardContent></Card>
          </div>

          {performanceLoading ? (
            <div className="py-12 flex items-center justify-center text-slate-500">
              <Loader2 className="w-6 h-6 mr-2 animate-spin" /> Cargando…
            </div>
          ) : !performanceReport?.employees?.length ? (
            <Card className="border-slate-200 bg-white">
              <CardContent className="py-16 text-center text-slate-500" data-testid="perf-empty">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Sin movimientos en este período.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="border-slate-200 bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <BarChart3 className="w-4 h-4" /> Participación por cantidad
                    </CardTitle>
                    <CardDescription>Clientes, servicios y productos por empleado.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={performanceReport.employees.map((e) => ({
                        name: e.barber_name,
                        Clientes: e.clients_count,
                        Servicios: e.services_count,
                        Productos: e.products_count,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip />
                        <Bar dataKey="Clientes" fill="#10b981" />
                        <Bar dataKey="Servicios" fill="#0ea5e9" />
                        <Bar dataKey="Productos" fill="#f59e0b" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Trophy className="w-4 h-4" /> Participación por valor (%)
                    </CardTitle>
                    <CardDescription>Share del total generado por cada empleado.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={320}>
                      <PieChart>
                        <Pie
                          data={performanceReport.employees.map((e) => ({ name: e.barber_name, value: e.total_value }))}
                          dataKey="value"
                          nameKey="name"
                          outerRadius={110}
                          label={(entry) => `${entry.name} ${((entry.percent || 0) * 100).toFixed(0)}%`}
                        >
                          {performanceReport.employees.map((_, idx) => (
                            <Cell key={idx} fill={['#10b981','#0ea5e9','#f59e0b','#8b5cf6','#ec4899','#14b8a6','#f43f5e','#6366f1'][idx % 8]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-slate-200 bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Users className="w-4 h-4" /> Detalle por empleado
                  </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table className="data-table" data-testid="perf-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Empleado</TableHead>
                        <TableHead className="text-right">Clientes</TableHead>
                        <TableHead className="text-right">Servicios</TableHead>
                        <TableHead className="text-right">Productos</TableHead>
                        <TableHead className="text-right">Club</TableHead>
                        <TableHead className="text-right">$ Servicios</TableHead>
                        <TableHead className="text-right">$ Productos</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {performanceReport.employees.map((e, idx) => {
                        const isOpen = perfExpanded === e.barber_id;
                        return (
                          <>
                            <TableRow key={e.barber_id} data-testid={`perf-row-${e.barber_id}`}>
                              <TableCell className="font-mono text-slate-400">{idx + 1}</TableCell>
                              <TableCell>
                                <button
                                  type="button"
                                  onClick={() => setPerfExpanded(isOpen ? null : e.barber_id)}
                                  className="font-medium text-slate-800 hover:text-emerald-700 underline-offset-2 hover:underline inline-flex items-center gap-1"
                                  data-testid={`perf-row-toggle-${e.barber_id}`}
                                >
                                  {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                  {e.barber_name}
                                </button>
                              </TableCell>
                              <TableCell className="text-right">{e.clients_count}</TableCell>
                              <TableCell className="text-right">{e.services_count}</TableCell>
                              <TableCell className="text-right">{e.products_count}</TableCell>
                              <TableCell className="text-right">
                                {e.loyalty_enrolled_count > 0 ? (
                                  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 text-xs font-semibold" data-testid={`perf-loyalty-${e.barber_id}`}>
                                    🎁 {e.loyalty_enrolled_count}
                                  </span>
                                ) : <span className="text-slate-300">—</span>}
                              </TableCell>
                              <TableCell className="text-right text-sky-700">{formatCurrency(e.services_total)}</TableCell>
                              <TableCell className="text-right text-amber-700">{formatCurrency(e.products_total)}</TableCell>
                              <TableCell className="text-right font-bold text-emerald-700">{formatCurrency(e.total_value)}</TableCell>
                            </TableRow>
                            {isOpen && (
                              <TableRow key={`${e.barber_id}-detail`} data-testid={`perf-detail-${e.barber_id}`}>
                                <TableCell colSpan={9} className="bg-slate-50/60 p-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <div className="text-xs uppercase tracking-wide text-sky-700 font-semibold mb-2">
                                        Servicios realizados ({e.services_count})
                                      </div>
                                      {e.services_detail?.length ? (
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="text-[11px] text-slate-500">
                                              <th className="text-left pb-1">Servicio</th>
                                              <th className="text-right pb-1">Cant.</th>
                                              <th className="text-right pb-1">Valor</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {e.services_detail.map((s, i) => (
                                              <tr key={i} className="border-t border-slate-200">
                                                <td className="py-1 text-slate-700">{s.name}</td>
                                                <td className="py-1 text-right text-slate-600">{s.qty}</td>
                                                <td className="py-1 text-right text-sky-700 font-medium">{formatCurrency(s.total)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      ) : (
                                        <p className="text-xs text-slate-400">Sin servicios en el período.</p>
                                      )}
                                    </div>
                                    <div>
                                      <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-2">
                                        Productos vendidos ({e.products_count})
                                      </div>
                                      {e.products_detail?.length ? (
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="text-[11px] text-slate-500">
                                              <th className="text-left pb-1">Producto</th>
                                              <th className="text-right pb-1">Cant.</th>
                                              <th className="text-right pb-1">Valor</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {e.products_detail.map((p, i) => (
                                              <tr key={i} className="border-t border-slate-200">
                                                <td className="py-1 text-slate-700">{p.name}</td>
                                                <td className="py-1 text-right text-slate-600">{p.qty}</td>
                                                <td className="py-1 text-right text-amber-700 font-medium">{formatCurrency(p.total)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      ) : (
                                        <p className="text-xs text-slate-400">Sin productos en el período.</p>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ===================== TOP CLIENTES TAB ===================== */}
        <TabsContent value="top-clientes" className="mt-4 space-y-4" data-testid="top-clientes-content">
          <RankFilters
            preset={rankPreset}
            startDate={rankStartDate}
            endDate={rankEndDate}
            onPreset={handleRankPreset}
            onStart={(v) => { setRankStartDate(v); setRankPreset('custom'); }}
            onEnd={(v) => { setRankEndDate(v); setRankPreset('custom'); }}
          />

          <Card className="border-slate-200 bg-white">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <Label className="text-sm text-slate-600">Mostrar top:</Label>
              <div className="w-28">
                <Select value={String(topClientsLimit)} onValueChange={(v) => setTopClientsLimit(Number(v))}>
                  <SelectTrigger data-testid="top-clients-limit" className="bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 20, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  disabled={!topClientsReport?.clients?.length}
                  onClick={() => {
                    const rows = (topClientsReport?.clients || []).map((c, i) => ({
                      pos: i + 1,
                      cliente: c.client_name,
                      telefono: c.phone || '—',
                      email: c.email || '—',
                      visitas: c.visits,
                      con_cita: c.with_appointment_visits,
                      walk_in: c.walk_in_visits,
                      servicios: c.services_count,
                      productos: c.products_count,
                      total_servicios: Number(c.services_total).toFixed(2),
                      total_productos: Number(c.products_total).toFixed(2),
                      total: Number(c.total_value).toFixed(2),
                      ultima_visita: c.last_visit ? formatDate(c.last_visit) : '—',
                    }));
                    exportToExcel(`Top_Clientes_${rankStartDate}_a_${rankEndDate}`, [{
                      name: 'Top Clientes',
                      columns: [
                        { header: '#', key: 'pos' },
                        { header: 'Cliente', key: 'cliente' },
                        { header: 'Teléfono', key: 'telefono' },
                        { header: 'Email', key: 'email' },
                        { header: 'Visitas', key: 'visitas' },
                        { header: 'Con cita', key: 'con_cita' },
                        { header: 'Walk-in', key: 'walk_in' },
                        { header: 'Servicios', key: 'servicios' },
                        { header: 'Productos', key: 'productos' },
                        { header: '$ Servicios', key: 'total_servicios' },
                        { header: '$ Productos', key: 'total_productos' },
                        { header: 'Total', key: 'total' },
                        { header: 'Última visita', key: 'ultima_visita' },
                      ],
                      rows,
                    }]);
                    toast.success('Excel descargado');
                  }}
                  data-testid="export-top-clients-excel"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
                </Button>
                <Button size="sm" variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  disabled={!topClientsReport?.clients?.length}
                  onClick={() => {
                    const rows = (topClientsReport?.clients || []).map((c, i) => ({
                      pos: String(i + 1),
                      cliente: c.client_name,
                      visitas: String(c.visits),
                      servicios: String(c.services_count),
                      productos: String(c.products_count),
                      total: Number(c.total_value).toFixed(2),
                    }));
                    exportToPDF(`Top_Clientes_${rankStartDate}_a_${rankEndDate}`, {
                      businessName: selectedBusiness?.name,
                      title: `Top ${topClientsLimit} Clientes`,
                      subtitle: `Período: ${rankStartDate} → ${rankEndDate}`,
                      sections: [{
                        title: 'Ranking',
                        columns: [
                          { header: '#', key: 'pos' },
                          { header: 'Cliente', key: 'cliente' },
                          { header: 'Visitas', key: 'visitas' },
                          { header: 'Servicios', key: 'servicios' },
                          { header: 'Productos', key: 'productos' },
                          { header: 'Total', key: 'total' },
                        ],
                        rows,
                      }],
                    });
                    toast.success('PDF descargado');
                  }}
                  data-testid="export-top-clients-pdf"
                >
                  <FileText className="w-3 h-3 mr-1" /> PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Clientes</div>
              <div className="text-2xl font-bold mt-1" data-testid="top-clients-kpi-clients">{topClientsReport?.totals?.clients ?? 0}</div>
            </CardContent></Card>
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Visitas</div>
              <div className="text-2xl font-bold mt-1 text-sky-600" data-testid="top-clients-kpi-visits">{topClientsReport?.totals?.visits ?? 0}</div>
            </CardContent></Card>
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Servicios</div>
              <div className="text-2xl font-bold mt-1 text-indigo-600" data-testid="top-clients-kpi-services">{topClientsReport?.totals?.services ?? 0}</div>
            </CardContent></Card>
            <Card className="border-slate-200 bg-white"><CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Total generado</div>
              <div className="text-2xl font-bold mt-1 text-emerald-600" data-testid="top-clients-kpi-total">{formatCurrency(topClientsReport?.totals?.total_value ?? 0)}</div>
            </CardContent></Card>
          </div>

          {topClientsLoading ? (
            <div className="py-12 flex items-center justify-center text-slate-500">
              <Loader2 className="w-6 h-6 mr-2 animate-spin" /> Cargando…
            </div>
          ) : !topClientsReport?.clients?.length ? (
            <Card className="border-slate-200 bg-white">
              <CardContent className="py-16 text-center text-slate-500" data-testid="top-clients-empty">
                <Trophy className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Sin actividad de clientes en este período.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="top-clients-list">
              {topClientsReport.clients.map((c, idx) => (
                <Card key={c.client_id} className="border-slate-200 bg-white" data-testid={`top-client-${c.client_id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      <div className="flex items-start gap-3 min-w-[220px]">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${idx===0?'bg-amber-500':idx===1?'bg-slate-400':idx===2?'bg-amber-700':'bg-slate-300'}`}>
                          {idx + 1}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-800">{c.client_name}</div>
                          <div className="text-xs text-slate-500">{c.phone || 'Sin teléfono'}</div>
                          {c.email && <div className="text-xs text-slate-500">{c.email}</div>}
                          <Link to={`/clientes?focus=${c.client_id}`}>
                            <Button size="sm" variant="outline" className="mt-2 text-xs h-7 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`top-client-history-${c.client_id}`}>
                              <History className="w-3 h-3 mr-1" /> Ver historial
                            </Button>
                          </Link>
                        </div>
                      </div>

                      <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">Visitas</div>
                          <div className="text-xl font-bold text-slate-800">{c.visits}</div>
                          <div className="text-[10px] text-slate-500">{c.with_appointment_visits} con cita · {c.walk_in_visits} walk-in</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">Servicios</div>
                          <div className="text-xl font-bold text-sky-600">{c.services_count}</div>
                          <div className="text-[10px] text-slate-500">{formatCurrency(c.services_total)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">Productos</div>
                          <div className="text-xl font-bold text-amber-600">{c.products_count}</div>
                          <div className="text-[10px] text-slate-500">{formatCurrency(c.products_total)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">Total</div>
                          <div className="text-xl font-bold text-emerald-600">{formatCurrency(c.total_value)}</div>
                          <div className="text-[10px] text-slate-500">
                            {c.last_visit ? `Última: ${formatDate(c.last_visit)}` : '—'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {(c.services?.length > 0 || c.products?.length > 0) && (
                      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {c.services?.length > 0 && (
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1.5">Servicios realizados</div>
                            <div className="flex flex-wrap gap-1.5">
                              {c.services.slice(0, 10).map((s, i) => (
                                <span key={i} className="text-[11px] bg-sky-50 text-sky-800 border border-sky-200 rounded px-2 py-0.5" data-testid={`service-chip-${c.client_id}-${i}`}>
                                  {s.name} <span className="text-sky-500">× {s.barber_name}</span>
                                </span>
                              ))}
                              {c.services.length > 10 && <span className="text-[11px] text-slate-500">+{c.services.length - 10} más</span>}
                            </div>
                          </div>
                        )}
                        {c.products?.length > 0 && (
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1.5">Productos adquiridos</div>
                            <div className="flex flex-wrap gap-1.5">
                              {c.products.slice(0, 10).map((p, i) => (
                                <span key={i} className="text-[11px] bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-0.5">
                                  {p.name} <span className="text-amber-600">× {p.qty}</span>
                                </span>
                              ))}
                              {c.products.length > 10 && <span className="text-[11px] text-slate-500">+{c.products.length - 10} más</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>


      </Tabs>

      {/* Payroll History Dialog */}
      <Dialog
        open={!!historyBarber}
        onOpenChange={(open) => {
          if (!open) {
            setHistoryBarber(null);
            setHistoryData(null);
          }
        }}
      >
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          data-testid="payroll-history-dialog"
        >
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Historial de pagos — {historyBarber?.display_name || ''}
            </DialogTitle>
            <DialogDescription>
              Registro cronológico de cada pago de nómina realizado a este barbero.
            </DialogDescription>
          </DialogHeader>

          {historyLoading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : historyData?.count === 0 ? (
            <div
              className="text-center py-12 text-slate-400 text-sm"
              data-testid="payroll-history-empty"
            >
              Aún no hay pagos de nómina registrados para este barbero.
            </div>
          ) : historyData ? (
            <div className="space-y-4">
              {/* Totals bar */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-amber-50 p-3 border border-amber-100">
                  <p className="text-[10px] uppercase tracking-wide text-amber-700">Comisiones</p>
                  <p className="text-lg font-bold text-amber-700">
                    {formatCurrency(historyData.totals.commissions_amount)}
                  </p>
                </div>
                <div className="rounded-lg bg-emerald-50 p-3 border border-emerald-100">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700">Propinas</p>
                  <p className="text-lg font-bold text-emerald-700">
                    {formatCurrency(historyData.totals.tips_amount)}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-100 p-3 border border-slate-200">
                  <p className="text-[10px] uppercase tracking-wide text-slate-600">Total pagado</p>
                  <p className="text-lg font-bold text-slate-900">
                    {formatCurrency(historyData.totals.total_paid)}
                  </p>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  data-testid="payroll-history-rows"
                >
                  <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2">Fecha de pago</th>
                      <th className="text-left py-2">Período</th>
                      <th className="text-right py-2">Comisiones</th>
                      <th className="text-right py-2">Propinas</th>
                      <th className="text-right py-2">Total</th>
                      <th className="text-left py-2">Por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(historyData.payments || []).map((p) => (
                      <tr
                        key={p.id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`payroll-row-${p.id}`}
                      >
                        <td className="py-2 text-slate-600">
                          {new Date(p.paid_at).toLocaleString('es-MX', {
                            timeZone: 'America/Mexico_City',
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="py-2 text-xs text-slate-500">
                          {p.period_start}
                          <br />→ {p.period_end}
                        </td>
                        <td className="py-2 text-right">
                          <span className="font-medium text-amber-700">
                            {formatCurrency(p.commissions_amount)}
                          </span>
                          <span className="block text-[10px] text-slate-400">
                            {p.commissions_count} regs
                          </span>
                        </td>
                        <td className="py-2 text-right">
                          <span className="font-medium text-emerald-700">
                            {formatCurrency(p.tips_amount)}
                          </span>
                          <span className="block text-[10px] text-slate-400">
                            {p.tips_count} regs
                          </span>
                        </td>
                        <td className="py-2 text-right font-bold text-slate-900">
                          {formatCurrency(p.total_paid)}
                        </td>
                        <td className="py-2 text-xs text-slate-600">
                          {p.paid_by_name || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Audit dialog — history of edits on a sale */}
      <Dialog open={!!auditSale} onOpenChange={(o) => !o && setAuditSale(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5" />
              Auditoría de la venta
            </DialogTitle>
          </DialogHeader>

          {auditSale && (
            <div className="text-xs text-slate-500 mb-2">
              Ticket <span className="font-mono text-slate-700">{auditSale.ticket_number}</span>
              {' · '}
              {auditSale.client_name || 'Sin cliente'}
              {' · '}
              Total <span className="font-semibold text-slate-700">{formatCurrency(auditSale.total)}</span>
            </div>
          )}

          {auditLoading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Cargando bitácora…
            </div>
          ) : auditEntries.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm" data-testid="tx-audit-empty">
              Esta venta no tiene cambios registrados.
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Cambio</TableHead>
                    <TableHead>Caja</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditEntries.map((a) => {
                    const isTotalChange = ['total_requested', 'total_approved', 'total_rejected'].includes(a.field);
                    return (
                      <TableRow key={a.id} data-testid={`tx-audit-entry-${a.id}`}>
                        <TableCell className="text-xs">{formatDateTime(a.created_at)}</TableCell>
                        <TableCell className="text-sm">{a.changed_by_name || a.changed_by || '—'}</TableCell>
                        <TableCell className="text-sm">
                          {isTotalChange ? (
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1">
                                {a.field === 'total_requested' && (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
                                    <ShieldAlert className="w-3 h-3" /> Cambio solicitado
                                  </Badge>
                                )}
                                {a.field === 'total_approved' && (
                                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
                                    <ShieldCheck className="w-3 h-3" /> Aprobado
                                  </Badge>
                                )}
                                {a.field === 'total_rejected' && (
                                  <Badge className="bg-red-100 text-red-800 border-red-200 gap-1">
                                    <ShieldX className="w-3 h-3" /> Rechazado
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs">
                                <span className="line-through text-slate-400">{formatCurrency(a.old_value)}</span>
                                <span className="mx-1">→</span>
                                <span className="font-bold text-slate-700">{formatCurrency(a.new_value)}</span>
                              </div>
                              {a.reason && <div className="text-[11px] text-slate-500 italic">&ldquo;{a.reason}&rdquo;</div>}
                              {a.notes && <div className="text-[11px] text-slate-500">Nota: {a.notes}</div>}
                            </div>
                          ) : (
                            <>
                              <span className="text-slate-500">{a.field}:</span>{' '}
                              <Badge variant="outline" className="text-slate-600 border-slate-200 mr-1">
                                {paymentMethodLabels[a.old_value] || a.old_value}
                              </Badge>
                              →
                              <Badge variant="outline" className="text-emerald-700 border-emerald-200 ml-1">
                                {paymentMethodLabels[a.new_value] || a.new_value}
                              </Badge>
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {a.register_adjustment === '+cash' && (
                            <span className="text-emerald-600">+ {formatCurrency(a.amount)} efectivo</span>
                          )}
                          {a.register_adjustment === '-cash' && (
                            <span className="text-red-500">- {formatCurrency(a.amount)} efectivo</span>
                          )}
                          {a.register_adjustment === 'none' && (
                            <span className="text-slate-400">sin ajuste</span>
                          )}
                          {isTotalChange && !a.register_adjustment && <span className="text-slate-400">—</span>}
                          {!a.register_was_open && a.register_adjustment && a.register_adjustment !== 'none' && (
                            <p className="text-[10px] text-amber-600 mt-0.5">caja cerrada</p>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditSale(null)} className="border-slate-200">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit total dialog — admin request, master admin authorization */}
      <Dialog open={!!editTotalSale} onOpenChange={(o) => !o && closeEditTotal()}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Editar monto cobrado
            </DialogTitle>
          </DialogHeader>

          {editTotalSale && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Ticket</span>
                  <span className="font-mono">{editTotalSale.ticket_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cliente</span>
                  <span>{editTotalSale.client_name || 'Sin cliente'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total actual</span>
                  <span className="font-bold">{formatCurrency(editTotalSale.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Método</span>
                  <span>{paymentMethodLabels[editTotalSale.payment_method] || editTotalSale.payment_method}</span>
                </div>
              </div>

              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  El nuevo monto se aplicará <strong>de inmediato</strong> a la venta y a la caja
                  (si es efectivo), pero <strong>quedará pendiente</strong> hasta que el master admin
                  autorice. Las comisiones sólo se recalcularán al aprobar.
                </span>
              </div>

              <div className="space-y-2">
                <Label>Nuevo monto</Label>
                <Input
                  type="number"
                  value={newTotalInput}
                  onChange={(e) => setNewTotalInput(e.target.value)}
                  className="bg-slate-100 border-slate-200"
                  min={0}
                  step={0.01}
                  data-testid="tx-edit-total-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Motivo del cambio <span className="text-red-500">*</span></Label>
                <Textarea
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  className="bg-slate-100 border-slate-200 resize-none"
                  placeholder="Ej. Cliente cuestionó el cobro, descuento no aplicado, etc."
                  rows={3}
                  data-testid="tx-edit-total-reason-input"
                />
                <p className="text-[11px] text-slate-500">
                  Este motivo queda registrado en la auditoría y visible para el master admin.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeEditTotal} disabled={editSubmitting}>
              Cancelar
            </Button>
            <Button
              onClick={submitEditTotal}
              disabled={editSubmitting}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="tx-edit-total-submit-btn"
            >
              {editSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Solicitar cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel sale dialog — admin request, master admin authorization */}
      <Dialog open={!!cancelSale} onOpenChange={(o) => !o && closeCancelSale()}>
        <DialogContent className="bg-white max-w-md" data-testid="tx-cancel-sale-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Eliminar venta
            </DialogTitle>
          </DialogHeader>

          {cancelSale && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Ticket</span>
                  <span className="font-mono">{cancelSale.ticket_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cliente</span>
                  <span>{cancelSale.client_name || 'Sin cliente'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total</span>
                  <span className="font-bold">{formatCurrency(cancelSale.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Método</span>
                  <span>{paymentMethodLabels[cancelSale.payment_method] || cancelSale.payment_method}</span>
                </div>
              </div>

              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-800 flex gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Al confirmar, la venta se marcará como <strong>cancelada</strong> de inmediato,
                  se devolverá el <strong>stock</strong>, se anularán las <strong>comisiones</strong> y
                  <strong> sellos de lealtad</strong>, y se ajustará la <strong>caja</strong> (si fue en
                  efectivo). Queda <strong>pendiente de autorización</strong> del master admin.
                </span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tx-cancel-reason-textarea">
                  Motivo <span className="text-red-600">*</span>
                </Label>
                <Textarea
                  id="tx-cancel-reason-textarea"
                  rows={3}
                  value={cancelReasonInput}
                  onChange={(e) => setCancelReasonInput(e.target.value)}
                  placeholder="Ej.: Venta duplicada por error de captura"
                  className="bg-slate-50"
                  data-testid="tx-cancel-reason-input"
                />
                <p className="text-[10px] text-slate-500">
                  Mínimo 3 caracteres. Este motivo aparecerá en el historial de auditoría.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeCancelSale}
              disabled={cancelSubmitting}
              data-testid="tx-cancel-sale-close-btn"
            >
              Cerrar
            </Button>
            <Button
              onClick={submitCancelSale}
              disabled={cancelSubmitting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="tx-cancel-sale-submit-btn"
            >
              {cancelSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmar eliminación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small internal component for the date range filter used by both Rankings and Clientes tabs
function RankFilters({ preset, startDate, endDate, onPreset, onStart, onEnd }) {
  const presets = [
    { v: '7', label: '7 días' },
    { v: '30', label: '30 días' },
    { v: '90', label: '90 días' },
  ];
  return (
    <Card className="border-slate-200 bg-white">
      <CardContent className="p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          {presets.map((p) => (
            <Button
              key={p.v}
              size="sm"
              variant={preset === p.v ? 'default' : 'outline'}
              className={preset === p.v ? '' : 'border-slate-200 text-slate-600'}
              onClick={() => onPreset(p.v)}
              data-testid={`preset-${p.v}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Label className="text-xs text-slate-500">Desde</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => onStart(e.target.value)}
            className="bg-slate-100 border-slate-200 h-9 w-40"
            data-testid="range-start"
          />
          <Label className="text-xs text-slate-500">Hasta</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => onEnd(e.target.value)}
            className="bg-slate-100 border-slate-200 h-9 w-40"
            data-testid="range-end"
          />
        </div>
      </CardContent>
    </Card>
  );
}
